// ============================================================
//  ★ 데이터 SEAM — 개발자 작업은 "여기"만 ★
// ------------------------------------------------------------
//  변환 산출물의 유일한 데이터 접점. fn 내부를 2.0 API 호출로 교체했다.
//  (핸드오프 규칙 — 컬럼/필터/테이블 UI 는 훅 계약(contents/totalCount) 그대로 소비)
//  BE: GET /api/v1/common-codes (LOGISPRD-9620, MOMS_COMMON_CODE)
//  - shipperCode 지정 시 전체공통('ALL') 포함, codeGrp/code 는 완전일치(eq) — 1.0 FILTER_OPT=EQUAL 보존
// ============================================================
import { useMemo, useRef } from 'react'
import request from '@/utils/request'
import { appendedRows, useFetchAllRows, type FetchedChunk, type UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { SY07_FETCH_CHUNK_SIZE } from '../consts'
import { formatDateTime } from '@/pages/master/userManagement/constants'

export interface CommonCodeRow {
  /** 수정(PUT /{id}) 대상 식별자 — 2.0 응답 id. 화면 표시엔 미사용 */
  _id: number
  SPR_NM: string
  CODE_GRP: string
  CODE: string
  CODE_DESC: string
  INS_DATE: string
  INS_BY: string
  UPD_DATE: string
  UPD_BY: string
}

export interface CommonCodeParams {
  shipperCode?: string
  /** 코드그룹 필터 — 다건 입력(string[]). 단건이면 서버 eq, 다건이면 클라이언트 IN 필터 */
  codeGrps?: string[]
  /** 코드 필터 — 다건 입력(string[]). 단건이면 서버 eq, 다건이면 클라이언트 IN 필터 */
  codes?: string[]
}

interface CommonCodeItem {
  id: number
  shipperCode: string
  code?: string | null
  codeDesc?: string | null
  codeGrp?: string | null
  // BE LocalDateTime 배열 직렬화([y,M,d,H,m,s]) — formatDateTime 이 배열/문자열 겸용 처리
  insDate?: string | number[] | null
  insBy?: string | null
  updDate?: string | number[] | null
  updBy?: string | null
}

const fmtDate = (v?: string | number[] | null) => (v == null ? '' : formatDateTime(v))

const toRow = (i: CommonCodeItem): CommonCodeRow => ({
  _id: i.id,
  SPR_NM: i.shipperCode,
  CODE_GRP: i.codeGrp ?? '',
  CODE: i.code ?? '',
  CODE_DESC: i.codeDesc ?? '',
  INS_DATE: fmtDate(i.insDate),
  INS_BY: i.insBy ?? '',
  UPD_DATE: fmtDate(i.updDate),
  UPD_BY: i.updBy ?? '',
})

/** BE 파라미터는 단수 eq — 단건이면 서버 필터, 다건이면 서버 생략 후 받은 행에서 IN 필터(inList) */
const fetchPage = async (params: CommonCodeParams, page: number, size: number | undefined): Promise<FetchedChunk<CommonCodeRow>> => {
  const grpList = params.codeGrps ?? []
  const codeList = params.codes ?? []
  const res = await request.get<
    ApiResponseV2<{ items: CommonCodeItem[]; pageInfo: { totalCount: number } }>,
    { shipperCode?: string; codeGrp?: string; code?: string; page: number; size?: number }
  >('/api/v1/common-codes', {
    shipperCode: params.shipperCode || undefined,
    codeGrp: grpList.length === 1 ? grpList[0] : undefined,
    code: codeList.length === 1 ? codeList[0] : undefined,
    page,
    size,
  })
  const items = (res.data?.items ?? []).map(toRow)
  return { items, totalCount: res.data?.pageInfo?.totalCount ?? items.length }
}

const inList = (v: string, list: string[] | undefined) => !list || list.length <= 1 || list.includes(v)
const getKey = (r: CommonCodeRow) => r._id

/** Query key — 도메인별 queryKeys 객체(CLAUDE.md). 맨 앞은 메뉴코드(탭 닫기 캐시 삭제). 무효화는 all, 조회는 list(params). 화주 옵션은 유저관리 API 재사용이라 여기 함께 둔다. */
export const commonCodeQueryKeys = {
  all: ['sy10', 'getCommonCodeList'] as const,
  list: (params: CommonCodeParams) => [...commonCodeQueryKeys.all, params] as const,
  shipperOptions: ['SY07_SHIPPER_OPTIONS'] as const,
}

/**
 * 전량 조회(LOGISPRD-15271) — CHUNK_SIZE 구간으로 끝까지 받는다. 실패 안내는 훅이 전역 알럿으로 한 번 낸다.
 * 다건 IN 필터는 구간 요청 밖에서 건다 — 안에서 거르면 구간이 CHUNK_SIZE 보다 짧아져 훅이 마지막 구간으로 오판하고 멈춘다.
 */
export const useGetCommonCodeList = (params: CommonCodeParams, enabled = true): UseFetchAllRowsResult<CommonCodeRow> => {
  const result = useFetchAllRows({
    queryKey: commonCodeQueryKeys.list(params),
    fetchPage: (page, size) => fetchPage(params, page, size),
    chunkSize: SY07_FETCH_CHUNK_SIZE,
    getKey,
    enabled,
  })
  const { codeGrps, codes } = params
  // 남은 구간이 붙으면 붙은 행만 걸러 덧붙인다 — 걸리는 행이 없으면 이전 배열을 그대로 돌려준다.
  //  매번 통째로 거르면 길이는 같고 참조만 바뀐 배열이 나가, 화면이 새 조회로 읽고 받는 동안 고친 셀을 지운다(appendedRows 계약).
  //  같은 입력으로 다시 불려도(StrictMode·동시 렌더의 memo 재실행) 이전 결과를 그대로 돌려준다 — 두 번째 실행이 통째로 다시 걸러 새 배열을 내지 않게(#1017 리뷰).
  const filterKey = JSON.stringify([codeGrps ?? null, codes ?? null]) // 조건은 값으로 본다 — 호출부가 렌더마다 새 배열을 넘겨도 같은 조건이다
  const filteredRef = useRef<{ src: CommonCodeRow[]; filterKey: string; out: CommonCodeRow[] }>({ src: [], filterKey: '', out: [] })
  const rows = useMemo(() => {
    const [grps, cds] = JSON.parse(filterKey) as [string[] | null, string[] | null]
    const match = (r: CommonCodeRow) => inList(r.CODE_GRP, grps ?? undefined) && inList(r.CODE, cds ?? undefined)
    const prev = filteredRef.current
    const sameFilter = prev.filterKey === filterKey
    if (sameFilter && prev.src === result.rows) return prev.out
    const tail = sameFilter ? appendedRows(prev.src, result.rows) : null
    const added = tail?.filter(match)
    const out = !tail ? result.rows.filter(match) : added?.length ? [...prev.out, ...added] : prev.out
    filteredRef.current = { src: result.rows, filterKey, out }
    return out
  }, [result.rows, filterKey])
  return { ...result, rows }
}
